import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { PLATFORMS, FIT_METHODS, FIT_BG, OUTPUT_FORMATS, MAX_FILES, getPlatform, getPreset } from "@/lib/presets";
import { loadImage, processImage, makeThumbnail, downloadDataUrl, dataUrlBytes } from "@/lib/imageProcessor";
import { addHistory, getPreferences, updatePreferences } from "@/lib/api";
import {
  UploadSimple, X, Download, Image as ImageIcon, ArrowsClockwise,
  CaretDown, FilePlus, Trash, Sparkle, GoogleLogo,
} from "@phosphor-icons/react";

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function Studio() {
  const { user, login } = useAuth();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]); // [{id,name,img,dataUrl,size}]
  const [activeId, setActiveId] = useState(null);
  const [platformId, setPlatformId] = useState("instagram");
  const [presetId, setPresetId] = useState("post-square");
  const [customMode, setCustomMode] = useState(false);
  const [customW, setCustomW] = useState(1080);
  const [customH, setCustomH] = useState(1080);
  const [fitMethod, setFitMethod] = useState("auto-crop");
  const [fitBg, setFitBg] = useState("white");
  const [format, setFormat] = useState("jpeg");
  const [quality, setQuality] = useState(90);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState({}); // {fileId: {dataUrl, size}}

  const platform = useMemo(() => getPlatform(platformId), [platformId]);
  const preset = useMemo(() => getPreset(platformId, presetId), [platformId, presetId]);
  const targetW = customMode ? Math.max(1, parseInt(customW) || 1) : preset.w;
  const targetH = customMode ? Math.max(1, parseInt(customH) || 1) : preset.h;

  // Load preferences once
  useEffect(() => {
    if (!user) return;
    getPreferences().then((p) => {
      if (!p) return;
      setPlatformId(p.default_platform || "instagram");
      setPresetId(p.default_preset || "post-square");
      setFormat(p.default_format || "jpeg");
      setQuality(p.default_quality ?? 90);
      setFitMethod(p.default_fit_method || "auto-crop");
      setFitBg(p.default_fit_bg || "white");
    }).catch(() => {});
  }, [user]);

  const addFiles = useCallback(async (fileList) => {
    const arr = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    const room = MAX_FILES - files.length;
    const accepted = arr.slice(0, room);
    const newItems = [];
    for (const f of accepted) {
      try {
        const { img, dataUrl } = await loadImage(f);
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        newItems.push({ id, name: f.name, img, dataUrl, size: f.size, w: img.width, h: img.height });
      } catch (e) {
        console.error("Failed to load", f.name, e);
      }
    }
    setFiles((prev) => {
      const next = [...prev, ...newItems];
      if (!activeId && next.length) setActiveId(next[0].id);
      return next;
    });
  }, [files.length, activeId]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setResults((prev) => {
      const c = { ...prev };
      delete c[id];
      return c;
    });
    if (activeId === id) setActiveId(null);
  };

  const clearAll = () => {
    setFiles([]);
    setResults({});
    setActiveId(null);
  };

  // Process active preview live
  const activeFile = files.find((f) => f.id === activeId) || files[0];
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!activeFile) { setPreview(null); return; }
    try {
      const { dataUrl } = processImage({
        img: activeFile.img,
        targetW, targetH,
        method: fitMethod,
        bg: fitBg,
        quality,
        format,
      });
      setPreview({ dataUrl, size: dataUrlBytes(dataUrl) });
    } catch (e) {
      console.error(e);
    }
  }, [activeFile, targetW, targetH, fitMethod, fitBg, quality, format]);

  const processAll = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const out = {};
      for (const f of files) {
        const { dataUrl } = processImage({
          img: f.img, targetW, targetH, method: fitMethod, bg: fitBg, quality, format,
        });
        out[f.id] = { dataUrl, size: dataUrlBytes(dataUrl) };
      }
      setResults(out);
      // record to history (only if logged in)
      if (user) {
        const fmtMeta = OUTPUT_FORMATS.find((x) => x.id === format);
        for (const f of files) {
          const r = out[f.id];
          const thumb = await makeThumbnail(r.dataUrl, 200);
          try {
            await addHistory({
              original_name: f.name,
              platform: platformId,
              preset: customMode ? "custom" : presetId,
              width: targetW,
              height: targetH,
              fit_method: fitMethod,
              output_format: format,
              quality,
              file_size: r.size,
              thumbnail: thumb,
            });
          } catch (_e) { /* swallow history errors */ }
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadOne = (f) => {
    const r = results[f.id];
    if (!r) return;
    const fmtMeta = OUTPUT_FORMATS.find((x) => x.id === format);
    const base = f.name.replace(/\.[^.]+$/, "");
    downloadDataUrl(r.dataUrl, `${base}-${targetW}x${targetH}.${fmtMeta.ext}`);
  };

  const downloadAll = () => {
    for (const f of files) {
      if (results[f.id]) downloadOne(f);
    }
  };

  const setAsDefault = async () => {
    if (!user) return;
    try {
      await updatePreferences({
        default_platform: platformId,
        default_preset: customMode ? "custom" : presetId,
        default_format: format,
        default_quality: quality,
        default_fit_method: fitMethod,
        default_fit_bg: fitBg,
      });
    } catch (_e) {}
  };

  return (
    <div data-testid="studio-page" className="max-w-7xl mx-auto px-4 md:px-10 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Studio</span>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight mt-1">Resize anything.</h1>
        </div>
        {!user && (
          <div className="flex items-center gap-3 chip" data-testid="anonymous-banner">
            <span>Working anonymously —</span>
            <button onClick={login} className="text-[#CCFF00] underline-offset-2 hover:underline inline-flex items-center gap-1">
              <GoogleLogo size={14} weight="bold" /> sign in to save history
            </button>
          </div>
        )}
      </div>

      {/* Uploader */}
      {files.length === 0 ? (
        <div
          data-testid="dropzone-empty"
          className={`dropzone ${dragActive ? "active" : ""} flex flex-col items-center justify-center p-12 md:p-20 text-center cursor-pointer`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
        >
          <div className="w-16 h-16 rounded-2xl bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center mb-5">
            <UploadSimple size={28} color="#CCFF00" weight="bold" />
          </div>
          <div className="font-display text-2xl md:text-3xl">Drop images here</div>
          <div className="text-zinc-400 mt-2">or click to browse — up to {MAX_FILES} files at once · JPG, PNG, WebP</div>
          <button data-testid="select-files-button" className="btn-volt mt-6">
            <FilePlus weight="bold" size={18} /> Select Images
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT — file queue */}
          <div className="lg:col-span-3 surface p-4 h-fit">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Queue · {files.length}/{MAX_FILES}</span>
              <button data-testid="clear-all-button" onClick={clearAll} className="text-xs text-zinc-500 hover:text-[#FF3366] transition flex items-center gap-1">
                <Trash size={14} /> Clear
              </button>
            </div>
            <div className="space-y-2 max-h-[480px] overflow-auto pr-1">
              {files.map((f) => (
                <div
                  key={f.id}
                  data-testid={`queue-item-${f.id}`}
                  onClick={() => setActiveId(f.id)}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                    activeId === f.id ? "bg-[#CCFF00]/10 border border-[#CCFF00]/30" : "border border-transparent hover:bg-zinc-900"
                  }`}
                >
                  <img src={f.dataUrl} alt={f.name} className="w-12 h-12 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{f.name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">{f.w}×{f.h} · {formatBytes(f.size)}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                    className="text-zinc-500 hover:text-[#FF3366] p-1"
                    data-testid={`remove-${f.id}`}
                    aria-label="Remove"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              data-testid="add-more-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={files.length >= MAX_FILES}
              className="btn-ghost w-full mt-4 justify-center disabled:opacity-40"
            >
              <FilePlus size={16} /> Add more
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
          </div>

          {/* CENTER — canvas preview */}
          <div className="lg:col-span-6 surface p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Preview</span>
              <span className="text-xs font-mono text-zinc-500">{targetW} × {targetH} · {format.toUpperCase()}{format !== "png" ? ` · q${quality}` : ""}</span>
            </div>
            <div className="checkerboard rounded-xl flex items-center justify-center overflow-hidden p-4 min-h-[420px]">
              {preview ? (
                <img
                  data-testid="preview-image"
                  src={preview.dataUrl}
                  alt="preview"
                  className="max-w-full max-h-[60vh] preview-shadow rounded-md"
                  style={{ aspectRatio: `${targetW}/${targetH}`, objectFit: "contain" }}
                />
              ) : (
                <div className="text-zinc-500">Select an image to preview</div>
              )}
            </div>
            {preview && (
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-500 font-mono">
                <span>~ {formatBytes(preview.size)}</span>
                <span>{platform.name} / {customMode ? "Custom" : preset.label}</span>
              </div>
            )}
          </div>

          {/* RIGHT — controls */}
          <div className="lg:col-span-3 space-y-4">
            <div className="surface p-4">
              <Label>Platform</Label>
              <select
                data-testid="platform-select"
                value={platformId}
                onChange={(e) => {
                  setPlatformId(e.target.value);
                  const p = getPlatform(e.target.value);
                  setPresetId(p.presets[0].id);
                  setCustomMode(false);
                }}
                className="input-base mt-2"
              >
                {PLATFORMS.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
              <Label className="mt-4">Size preset</Label>
              <select
                data-testid="preset-select"
                value={customMode ? "__custom" : presetId}
                onChange={(e) => {
                  if (e.target.value === "__custom") {
                    setCustomMode(true);
                  } else {
                    setCustomMode(false);
                    setPresetId(e.target.value);
                    const np = getPreset(platformId, e.target.value);
                    setCustomW(np.w); setCustomH(np.h);
                  }
                }}
                className="input-base mt-2"
              >
                {platform.presets.map((p) => (
                  <option key={p.id} value={p.id}>{p.label} — {p.w}×{p.h}</option>
                ))}
                <option value="__custom">Custom dimensions…</option>
              </select>

              {customMode && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500">Width</label>
                    <input data-testid="custom-w" type="number" min="1" max="8000" className="input-base mt-1" value={customW} onChange={(e) => setCustomW(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500">Height</label>
                    <input data-testid="custom-h" type="number" min="1" max="8000" className="input-base mt-1" value={customH} onChange={(e) => setCustomH(e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            <div className="surface p-4">
              <Label>Fit method</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {FIT_METHODS.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`fit-${m.id}`}
                    onClick={() => setFitMethod(m.id)}
                    className={`text-xs py-2 rounded-lg border transition ${
                      fitMethod === m.id ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    {m.label.split(" ")[0]}
                  </button>
                ))}
              </div>
              {fitMethod === "fit" && (
                <>
                  <Label className="mt-3">Background</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {FIT_BG.map((b) => (
                      <button
                        key={b.id}
                        data-testid={`bg-${b.id}`}
                        onClick={() => setFitBg(b.id)}
                        className={`text-xs py-2 rounded-lg border transition ${
                          fitBg === b.id ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                        }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="surface p-4">
              <Label>Output format</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {OUTPUT_FORMATS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`format-${f.id}`}
                    onClick={() => setFormat(f.id)}
                    className={`text-xs py-2 rounded-lg border transition ${
                      format === f.id ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {format !== "png" && (
                <>
                  <div className="flex items-center justify-between mt-4">
                    <Label>Quality</Label>
                    <span className="text-xs font-mono text-[#CCFF00]">{quality}</span>
                  </div>
                  <input
                    data-testid="quality-slider"
                    type="range"
                    min="10"
                    max="100"
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    className="w-full mt-2 accent-[#CCFF00]"
                  />
                </>
              )}
            </div>

            <button
              data-testid="process-all-button"
              onClick={processAll}
              disabled={busy || files.length === 0}
              className="btn-volt w-full disabled:opacity-50"
            >
              <ArrowsClockwise size={18} weight="bold" /> {busy ? "Processing…" : `Process ${files.length} ${files.length === 1 ? "image" : "images"}`}
            </button>

            {Object.keys(results).length > 0 && (
              <button data-testid="download-all-button" onClick={downloadAll} className="btn-ghost w-full justify-center">
                <Download size={18} /> Download all
              </button>
            )}

            {user && (
              <button data-testid="save-default-button" onClick={setAsDefault} className="text-xs text-zinc-500 hover:text-[#CCFF00] transition w-full text-center">
                Save current settings as defaults
              </button>
            )}
          </div>

          {/* RESULTS GRID */}
          {Object.keys(results).length > 0 && (
            <div className="lg:col-span-12 surface p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Results</span>
                <span className="text-xs font-mono text-zinc-500">{Object.keys(results).length} ready</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {files.map((f) => {
                  const r = results[f.id];
                  if (!r) return null;
                  return (
                    <div key={f.id} data-testid={`result-${f.id}`} className="rounded-xl border border-zinc-800 overflow-hidden bg-[#0a0a0a]">
                      <div className="checkerboard aspect-square flex items-center justify-center">
                        <img src={r.dataUrl} alt="" className="max-w-full max-h-full" style={{ objectFit: "contain" }} />
                      </div>
                      <div className="p-3">
                        <div className="text-xs truncate">{f.name}</div>
                        <div className="text-[11px] text-zinc-500 font-mono mt-1">{targetW}×{targetH} · {formatBytes(r.size)}</div>
                        <button onClick={() => downloadOne(f)} data-testid={`download-${f.id}`} className="btn-volt text-xs mt-3 w-full justify-center !py-2">
                          <Download size={14} weight="bold" /> Download
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Label({ children, className = "" }) {
  return <div className={`text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 ${className}`}>{children}</div>;
}
